import React from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { useEditReferralEmailFlowDialog } from '@/contexts/EditReferralEmailFlowDialogContext';
import type { ReferralCampaign } from '@/types/referral';
import { REFERRAL_MEMBER_STATUS_OPTIONS } from '@/types/referral';

export function GlobalEditReferralEmailFlowDialog() {
  const { isOpen, campaign, formData, setFormData, programs, closeEditFlow, onSave } = useEditReferralEmailFlowDialog();
  const [saving, setSaving] = React.useState(false);

  const buildTriggerConditions = () => {
    const tc = formData.trigger_conditions;
    if (!tc || (tc.member_status ?? []).length === 0) return null;
    return { member_status: (tc.member_status ?? []).filter(Boolean) };
  };

  const handleSave = async () => {
    if (!campaign || !onSave) return;
    setSaving(true);
    try {
      const result = await onSave(campaign.id, {
        name: formData.name,
        description: formData.description || null,
        status: formData.status as ReferralCampaign['status'],
        program_id: formData.program_id || null,
        flow_type: formData.flow_type,
        notes: formData.notes || null,
        variant_label: formData.variant_label || null,
        trigger_conditions: buildTriggerConditions() ?? undefined,
      });
      if (result != null) {
        closeEditFlow();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeEditFlow()}>
      <DialogContent
        className="max-h-[90vh] max-w-lg flex flex-col overflow-hidden gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitleWithBug popupName="Edit Email Flow" popupContext="/referral/campaigns" isOpen={isOpen}>
            Edit Email Flow
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-flow-name">Flow Name</Label>
            <Input
              id="edit-flow-name"
              value={formData.name}
              onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
              placeholder="Enter flow name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-type">Type</Label>
            <Select
              value={formData.flow_type}
              onValueChange={(v) => setFormData((d) => ({ ...d, flow_type: v as 'automatic' | 'manual' }))}
            >
              <SelectTrigger id="edit-flow-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="automatic">Automatic (runs on its own — no dispatch)</SelectItem>
                <SelectItem value="manual">Manual (initiated via Dispatch Center)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-description">Description</Label>
            <Textarea
              id="edit-flow-description"
              value={formData.description}
              onChange={(e) => setFormData((d) => ({ ...d, description: e.target.value }))}
              placeholder="Enter description"
              rows={2}
              className="resize-none"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-notes">Notes</Label>
            <Textarea
              id="edit-flow-notes"
              value={formData.notes}
              onChange={(e) => setFormData((d) => ({ ...d, notes: e.target.value }))}
              placeholder="e.g. Welcome, Re-engagement, Milestone"
              rows={2}
              className="resize-none"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-variant">Variant (A/B)</Label>
            <Input
              id="edit-flow-variant"
              value={formData.variant_label}
              onChange={(e) => setFormData((d) => ({ ...d, variant_label: e.target.value }))}
              placeholder="e.g. Control, Variant A"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-program">Program</Label>
            <Select
              value={formData.program_id || 'none'}
              onValueChange={(value) => setFormData((d) => ({ ...d, program_id: value === 'none' ? '' : value }))}
            >
              <SelectTrigger id="edit-flow-program">
                <SelectValue placeholder="No program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No program</SelectItem>
                {programs?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-flow-status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData((d) => ({ ...d, status: value }))}
            >
              <SelectTrigger id="edit-flow-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Enrollment triggers (optional)</h4>
            <p className="text-xs text-muted-foreground">Only enroll program members that match these conditions. Leave empty to enroll all.</p>
            <div>
              <Label className="text-xs">Member status</Label>
              <MultiSelect
                options={REFERRAL_MEMBER_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
                selected={formData.trigger_conditions?.member_status ?? []}
                onChange={(v) => setFormData((d) => ({ ...d, trigger_conditions: { ...(d.trigger_conditions ?? {}), member_status: v } }))}
                placeholder="Any"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={closeEditFlow}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
